import { Users, ActivityItem, Version } from './types'

export const USERS: Users = {
  maria: {
    name: 'Allison Harvey',
    initials: 'AH',
    color: '#7C3AED',
    role: 'submitter',
    title: 'Marketing Director',
  },
  james: {
    name: 'David Lam',
    initials: 'DL',
    color: '#2563EB',
    role: 'reviewer',
    title: 'Chief Compliance Officer',
  },
  sarah: {
    name: 'Rachel Kim',
    initials: 'RK',
    color: '#D946EF',
    role: 'reviewer',
    title: 'Compliance Counsel',
  },
}

export const ONLINE: string[] = ['maria', 'james']

export const ACTIVITY: ActivityItem[] = [
  { user: 'maria', action: 'uploaded Version 1', time: '10:15 AM' },
  {
    user: null,
    action: 'AI scan — 3 blockers, 3 suggestions.',
    time: '10:15 AM',
  },
  {
    user: 'james',
    action: 'commented on "Net performance missing"',
    time: '10:32 AM',
  },
  { user: 'maria', action: 'uploaded Version 2', time: '1:45 PM' },
  {
    user: null,
    action: 'AI scan — 1 blocker remaining.',
    time: '1:45 PM',
  },
  { user: 'maria', action: 'uploaded Version 3', time: '3:10 PM' },
  {
    user: null,
    action: 'AI scan — 0 blockers. Ready to submit.',
    time: '3:10 PM',
  },
  {
    user: 'maria',
    action: 'submitted for compliance review',
    time: '3:15 PM',
  },
  {
    user: 'james',
    action: 'assigned himself to review',
    time: '3:22 PM',
  },
  {
    user: 'james',
    action: 'marked "AUM as-of date" as N/A',
    time: '3:35 PM',
  },
]

export const VERSIONS: Version[] = [
  {
    id: 'v1',
    label: 'V1',
    time: '10:15 AM',
    blockers: [
      {
        id: 'b1',
        title: 'Gross performance shown without net',
        desc: '"18.7% return" shown without net-of-fees performance. Any gross must be accompanied by net at equal prominence.',
        fix: 'Add net-of-fee return alongside gross at same size. E.g. "18.7% gross / 17.1% net."',
        location: 'Performance hero',
        reg: 'Rule 206(4)-1(d)(2)',
        assignee: 'maria',
        comments: [
          {
            user: 'james',
            text: "Net has to be right next to gross, same font size. Can't footnote it.",
            time: '10:32 AM',
          },
        ],
      },
      {
        id: 'b2',
        title: 'No time period or benchmark',
        desc: '"18.7% return" lacks time period, benchmark, or strategy identification — reads as cherry-picked.',
        fix: 'Add time period (e.g. "CY 2024"), benchmark (S&P 500: 25.0%), and composite name.',
        location: 'Performance hero',
        reg: 'Rule 206(4)-1(d)(2), (d)(5)',
        assignee: 'maria',
        comments: [],
      },
      {
        id: 'b3',
        title: 'Client testimonial without disclosures',
        desc: "Sarah M.'s quote requires disclosure of: client status, compensation, and that it may not represent all experiences.",
        fix: 'Add after quote: "Current client. Not compensated. Experience may not be representative."',
        location: 'Testimonial',
        reg: 'Rule 206(4)-1(b)(1), (b)(4)',
        assignee: 'maria',
        comments: [],
      },
    ],
    suggestions: [
      {
        id: 's1',
        title: '"Award-Winning" needs methodology',
        desc: 'If referencing a specific ranking, must disclose criteria and whether firm paid to participate.',
        fix: 'Add footnote with ranking source, criteria, and fee status.',
        location: 'Headline',
        reg: 'Rule 206(4)-1(c)(1)',
        assignee: 'maria',
        comments: [],
      },
      {
        id: 's2',
        title: 'AUM missing as-of date',
        desc: '"$2.8B AUM" needs a date — AUM changes daily.',
        fix: 'Add "as of [date]" next to AUM.',
        location: 'Credentials',
        reg: 'Rule 206(4)-1(a)(2)',
        assignee: 'maria',
        comments: [],
      },
      {
        id: 's3',
        title: '"Personalized" may imply suitability',
        desc: '"Personalized strategies built around your goals" could imply suitability for a general audience.',
        fix: 'Consider: "Strategies tailored to individual client objectives."',
        location: 'Body copy',
        reg: 'Advisers Act §206',
        assignee: 'james',
        comments: [],
      },
    ],
    passed: 18,
    narrative: {
      marketing: {
        greeting:
          'Hey Allison — 3 things need to be fixed before this can go out.',
        sections: [
          {
            type: 'blockers',
            heading: "What's blocking this",
            content:
              "All three are about the performance claim and testimonial:\n\n• **18.7% needs net-of-fee performance right next to it** — same size, same prominence. The SEC is very literal about this.\n• **The 18.7% also needs a time period, benchmark, and strategy name.** Right now it's a number floating in space — reads as cherry-picked.\n• **Sarah M.'s quote is a testimonial under the Marketing Rule.** We need three disclosures: current client, not compensated, may not be representative.\n\nAll three must be resolved before you can submit.",
          },
          {
            type: 'suggestions',
            heading: 'Compliance will also check',
            content:
              'These won\'t block you:\n\n• "Award-Winning" — we\'ll need methodology and whether we paid to participate\n• "$2.8B AUM" needs an as-of date\n• "Personalized strategies" — David will review for implied suitability',
          },
        ],
        closing:
          'Fix the 3 blockers and upload a new version. 18 of 24 checks passed — not bad for a first draft.',
      },
      compliance: {
        greeting: '3 blockers. 3 suggestions. 18 of 24 passed.',
        sections: [
          {
            type: 'blockers',
            heading: 'Blockers (gated)',
            content:
              '• **206(4)-1(d)(2)** — Gross performance without net at equal prominence\n• **206(4)-1(d)(2), (d)(5)** — No time period, benchmark, or composite ID\n• **206(4)-1(b)(1), (b)(4)** — Testimonial missing required disclosures',
          },
          {
            type: 'suggestions',
            heading: 'Suggestions',
            content:
              '• **206(4)-1(c)(1)** — "Award-winning" methodology\n• **206(4)-1(a)(2)** — AUM without as-of date\n• **§206** — "Personalized strategies" in general audience context',
          },
        ],
        closing: 'Recommend return to submitter.',
      },
      executive: {
        greeting:
          '3 blockers on the Flagship Equity ad. Performance/testimonial gaps — fixable in one revision.',
        sections: [],
        closing: '',
      },
    },
    ad: {
      hasNet: false,
      netReturn: null,
      period: null,
      benchmark: null,
      strategy: null,
      testimonialDisc: false,
      aum: '$2.8B AUM',
      awardNote: false,
      footer:
        'Meridian Wealth Partners is a registered investment adviser. Investing involves risk.',
    },
  },
  {
    id: 'v2',
    label: 'V2',
    time: '1:45 PM',
    blockers: [
      {
        id: 'b3',
        title: 'Testimonial disclosures too small',
        desc: 'Disclosures added but at 6pt below the fold. Must be prominent enough to be noticed alongside the testimonial.',
        fix: 'Move directly below quote at body copy size.',
        location: 'Testimonial',
        reg: 'Rule 206(4)-1(b)(1)',
        assignee: 'maria',
        comments: [
          {
            user: 'james',
            text: 'Close — but prominence matters. Needs to read as part of the testimonial block.',
            time: '2:05 PM',
          },
          {
            user: 'maria',
            text: 'Will move it under the quote at body size.',
            time: '2:15 PM',
          },
        ],
      },
    ],
    suggestions: [
      {
        id: 's1',
        title: '"Award-Winning" — methodology pending',
        desc: 'Still needs ranking source and fee-for-participation disclosure.',
        fix: 'Add footnote.',
        location: 'Headline',
        reg: 'Rule 206(4)-1(c)(1)',
        assignee: 'maria',
        comments: [],
      },
      {
        id: 's3',
        title: '"Tailored" language — compliance to review',
        desc: 'Wording softened but David should confirm.',
        fix: 'Review in context.',
        location: 'Body copy',
        reg: 'Advisers Act §206',
        assignee: 'james',
        comments: [],
      },
    ],
    passed: 23,
    narrative: {
      marketing: {
        greeting: 'Nice work — down to 1 blocker.',
        sections: [
          {
            type: 'blockers',
            heading: 'Last blocker',
            content:
              "• **Testimonial disclosures are there but too small** — they're in 6pt below the fold. Move them directly under Sarah M.'s quote at body copy size.\n\nThat's the last thing blocking submission.",
          },
          {
            type: 'suggestions',
            heading: "Still on David's list",
            content:
              '• "Award-Winning" still needs the methodology footnote\n• David will review the "tailored" language',
          },
        ],
        closing:
          'You cleared the performance issues cleanly. One more tweak and you can submit.',
      },
      compliance: {
        greeting: '1 blocker. 2 suggestions. 23 passed.',
        sections: [
          {
            type: 'blockers',
            heading: 'Remaining',
            content:
              '• **206(4)-1(b)(1)** — Testimonial disclosures at insufficient prominence (6pt, below fold)',
          },
          {
            type: 'suggestions',
            heading: 'Suggestions',
            content:
              '• **206(4)-1(c)(1)** — Award methodology pending\n• **§206** — "Tailored" language needs final review',
          },
        ],
        closing: 'Performance issues resolved in V2.',
      },
      executive: {
        greeting:
          'Down from 3 blockers to 1. Quick fix — testimonial disclosure placement.',
        sections: [],
        closing: '',
      },
    },
    ad: {
      hasNet: true,
      netReturn: '17.1% net of fees',
      period: 'Calendar Year 2024',
      benchmark: 'S&P 500: 25.0%',
      strategy: 'Flagship Equity Composite',
      testimonialDisc: false,
      aum: '$2.8B AUM (as of 12/31/2024)',
      awardNote: false,
      footer:
        'Meridian Wealth Partners is a registered investment adviser. Investing involves risk, including loss of principal. Past performance is not indicative of future results.',
    },
  },
  {
    id: 'v3',
    label: 'V3',
    time: '3:10 PM',
    blockers: [],
    suggestions: [
      {
        id: 's1',
        title: '"Award-Winning" — methodology footnote',
        desc: "Barron's criteria and fee status should be disclosed.",
        fix: 'Verify footnote wording.',
        location: 'Footer',
        reg: 'Rule 206(4)-1(c)(1)',
        assignee: 'maria',
        comments: [
          {
            user: 'maria',
            text: "Added. Barron's doesn't charge — confirmed with BD.",
            time: '3:28 PM',
          },
        ],
      },
      {
        id: 's3',
        title: '"Tailored to individual objectives"',
        desc: 'Compliance to confirm no implied suitability.',
        fix: 'Review in context.',
        location: 'Body copy',
        reg: 'Advisers Act §206',
        assignee: 'james',
        comments: [],
      },
    ],
    passed: 25,
    narrative: {
      marketing: {
        greeting: 'No blockers — ready to submit. 🎯',
        sections: [
          {
            type: 'suggestions',
            heading: 'What compliance will review',
            content:
              'Two minor items:\n\n• **Barron\'s ranking footnote** — you added it, David will confirm wording\n• **"Tailored to individual objectives"** — David will check for implied suitability\n\nNeither blocks submission. Expect 2–4 hour turnaround.',
          },
        ],
        closing:
          'All 3 original blockers resolved across 2 revisions. Nice work.',
      },
      compliance: {
        greeting: '0 blockers. 2 suggestions. 25 passed.',
        sections: [
          {
            type: 'suggestions',
            heading: 'For review',
            content:
              '• **206(4)-1(c)(1)** — Verify Barron\'s methodology disclosure\n• **§206** — Review "tailored to individual objectives" for implied suitability',
          },
        ],
        closing:
          '3/3 blockers resolved across 2 revisions. Recommend expedited review.',
      },
      executive: {
        greeting:
          'Flagship Equity ad cleared. 2 minor items for David. Low risk.',
        sections: [],
        closing: '',
      },
    },
    ad: {
      hasNet: true,
      netReturn: '17.1% net of fees',
      period: 'Calendar Year 2024',
      benchmark: 'S&P 500: 25.0%',
      strategy: 'Flagship Equity Composite',
      testimonialDisc: true,
      aum: '$2.8B AUM (as of 12/31/2024)',
      awardNote: true,
      footer:
        "Meridian Wealth Partners is a registered investment adviser. Investing involves risk, including loss of principal. Past performance is not indicative of future results. *Barron's Top Advisor 2024, based on AUM, revenue, quality of practice. Meridian did not pay a fee.",
    },
  },
]
